import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminApi, type PlanAttributes } from '@/api/adminApi'
import type { Plan } from '@/api/types'
import { AdminCard, AdminInput, AdminPageHeader, Money } from '@/components/admin/AdminUi'
import { SafeActionDialog } from '@/components/admin/SafeActionDialog'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'

type Pending =
  | { kind: 'attributes'; plan: Plan; attributes: PlanAttributes }
  | { kind: 'price' | 'migration'; plan: Plan; priceCents: number }
  | null

function attributesFrom(plan: Plan): PlanAttributes {
  return {
    name: plan.name,
    description: plan.description ?? '',
    max_projects: plan.max_projects ?? 1,
    max_members: plan.max_members ?? 1,
    allow_version_history: Boolean(plan.allow_version_history),
    allow_team_members: Boolean(plan.allow_team_members),
    allow_export_import: Boolean(plan.allow_export_import),
    is_available_for_purchase: Boolean(plan.is_available_for_purchase),
    is_active: plan.is_active,
  }
}

export default function PlansPage() {
  const { t } = useTranslation()
  const [plans, setPlans] = useState<Plan[]>([])
  const [prices, setPrices] = useState<Record<number, number>>({})
  const [attributes, setAttributes] = useState<Record<number, PlanAttributes>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>(null)

  const load = useCallback(() => {
    setLoading(true)
    adminApi.plans()
      .then((response) => {
        setPlans(response.results)
        setPrices(Object.fromEntries(response.results.map((plan) => [plan.id, plan.price_cents])))
        setAttributes(Object.fromEntries(response.results.map((plan) => [plan.id, attributesFrom(plan)])))
        setError(null)
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  const updateAttribute = <K extends keyof PlanAttributes>(planId: number, key: K, value: PlanAttributes[K]) => {
    setAttributes((state) => ({ ...state, [planId]: { ...state[planId], [key]: value } }))
  }

  if (loading) return <div className="grid gap-3 md:grid-cols-3"><Skeleton height={360} /><Skeleton height={360} /><Skeleton height={360} /></div>
  if (error) return <EmptyState title={t('common.errorLoading', 'Erro ao carregar')} description={error} />

  return (
    <div>
      <AdminPageHeader
        title={t('admin.plans.title', 'Planos e preços')}
        description={t('admin.plans.description', 'Edite limites e recursos do plano. Preço novo e migração de contratos legados continuam como ações separadas.')}
      />
      <div className="grid gap-4 xl:grid-cols-3">
        {plans.map((plan) => {
          const draft = attributes[plan.id] ?? attributesFrom(plan)
          return (
            <AdminCard key={plan.id}>
              <div className="flex items-start justify-between gap-3">
                <span className="rounded px-2 py-1 text-[10px] uppercase" style={{ background: 'var(--bg-toolbar)' }}>{plan.code}</span>
                <span className="text-xl font-semibold"><Money cents={plan.price_cents} /><span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>/mês</span></span>
              </div>
              <h2 className="mt-4 text-xl font-semibold">{plan.name}</h2>
              <p className="mt-1 min-h-10 text-xs" style={{ color: 'var(--text-muted)' }}>{plan.description || 'Limites e recursos deste plano.'}</p>
              <div className="my-4 grid grid-cols-2 gap-3 rounded-lg p-3 text-xs" style={{ background: 'var(--bg-toolbar)' }}><div><strong className="block text-lg">{plan.max_projects}</strong>projetos</div><div><strong className="block text-lg">{plan.max_members}</strong>membros</div></div>
              <div className="mb-4 flex flex-wrap gap-2 text-xs" style={{ color: 'var(--text-muted)' }}><span>{plan.is_active ? 'Plano ativo' : 'Plano desativado'}</span><span>·</span><span>{plan.is_available_for_purchase ? 'Disponível para compra' : 'Fora de venda'}</span></div>
              <details className="border-t pt-3" style={{ borderColor: 'var(--border)' }}><summary className="cursor-pointer text-sm font-medium" style={{ color: 'var(--accent)' }}>Editar limites e recursos</summary>
              <div className="mt-4 grid gap-3">
                <label className="text-xs">Nome<AdminInput className="mt-1 w-full" value={draft.name} onChange={(event) => updateAttribute(plan.id, 'name', event.target.value)} /></label>
                <label className="text-xs">Descrição<textarea className="mt-1 min-h-20 w-full rounded border p-2 text-xs" style={{ background: 'var(--bg-input)', borderColor: 'var(--border)' }} value={draft.description} onChange={(event) => updateAttribute(plan.id, 'description', event.target.value)} /></label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs">Máx. projetos<AdminInput className="mt-1 w-full" type="number" min={1} value={draft.max_projects} onChange={(event) => updateAttribute(plan.id, 'max_projects', Number(event.target.value))} /></label>
                  <label className="text-xs">Máx. membros<AdminInput className="mt-1 w-full" type="number" min={1} value={draft.max_members} onChange={(event) => updateAttribute(plan.id, 'max_members', Number(event.target.value))} /></label>
                </div>
                <div className="grid gap-2 text-xs">
                  <Check label="Histórico de versões" checked={draft.allow_version_history} onChange={(value) => updateAttribute(plan.id, 'allow_version_history', value)} />
                  <Check label="Membros no workspace" checked={draft.allow_team_members} onChange={(value) => updateAttribute(plan.id, 'allow_team_members', value)} />
                  <Check label="Importar e exportar" checked={draft.allow_export_import} onChange={(value) => updateAttribute(plan.id, 'allow_export_import', value)} />
                  <Check label="Disponível para compra" checked={draft.is_available_for_purchase} onChange={(value) => updateAttribute(plan.id, 'is_available_for_purchase', value)} />
                  <Check label="Plano disponível" checked={draft.is_active} onChange={(value) => updateAttribute(plan.id, 'is_active', value)} />
                </div>
                <Button variant="primary" onClick={() => setPending({ kind: 'attributes', plan, attributes: draft })}>Revisar atributos</Button>
              </div>
              </details>
              {plan.price_cents > 0 ? (
                <div className="mt-5 border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                  <label className="block text-xs">Novo preço em centavos<AdminInput className="mt-1 w-full" type="number" min={1} value={prices[plan.id] ?? plan.price_cents} onChange={(event) => setPrices((state) => ({ ...state, [plan.id]: Number(event.target.value) }))} /></label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button onClick={() => setPending({ kind: 'price', plan, priceCents: prices[plan.id] })}>Revisar novo preço</Button>
                    <Button onClick={() => setPending({ kind: 'migration', plan, priceCents: plan.price_cents })}>Atualizar contratos antigos</Button>
                  </div>
                </div>
              ) : null}
            </AdminCard>
          )
        })}
      </div>
      <SafeActionDialog
        open={pending !== null}
        title={pending?.kind === 'attributes' ? 'Alterar atributos do plano' : pending?.kind === 'price' ? 'Alterar preço mensal' : 'Atualizar assinaturas com preço antigo'}
        confirmLabel={pending?.kind === 'attributes' ? 'Aplicar atributos' : pending?.kind === 'price' ? 'Criar e aplicar' : 'Migrar lote'}
        preview={() => {
          if (!pending) return Promise.reject(new Error('Ação inválida'))
          if (pending.kind === 'attributes') return adminApi.planAttributesPreview(pending.plan.id, pending.attributes)
          if (pending.kind === 'price') return adminApi.pricePreview(pending.plan.id, pending.priceCents)
          return adminApi.migrationPreview(pending.plan.id)
        }}
        apply={(request) => {
          if (!pending) return Promise.reject(new Error('Ação inválida'))
          if (pending.kind === 'attributes') return adminApi.planAttributesApply(pending.plan.id, pending.attributes, request)
          if (pending.kind === 'price') return adminApi.priceApply(pending.plan.id, pending.priceCents, request)
          return adminApi.migrationApply(pending.plan.id, 50, request)
        }}
        onClose={() => setPending(null)}
        onApplied={load}
      />
    </div>
  )
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return <label className="flex items-center gap-2"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />{label}</label>
}
